import React, { useState } from 'react';
import { saveBodyProfile } from '../services/userService';

const womenShapes = [
  {
    id: 'hourglass',
    name: 'Hourglass',
    emoji: '⌛',
    description: 'Balanced shoulders & hips, defined waist',
    tip: 'Fitted styles that highlight your waist work best',
  },
  {
    id: 'pear',
    name: 'Pear',
    emoji: '🍐',
    description: 'Hips wider than shoulders',
    tip: 'A-line skirts and wide neck tops balance your shape',
  },
  {
    id: 'apple',
    name: 'Apple',
    emoji: '🍎',
    description: 'Fuller midsection, slimmer legs',
    tip: 'Empire waist and flowy styles are most flattering',
  },
  {
    id: 'rectangle',
    name: 'Rectangle',
    emoji: '▭',
    description: 'Similar shoulder, waist and hip width',
    tip: 'Peplum and ruffles add beautiful curves',
  },
  {
    id: 'inverted_triangle',
    name: 'Inverted Triangle',
    emoji: '🔺',
    description: 'Shoulders wider than hips',
    tip: 'Wide leg pants and A-line skirts balance proportions',
  },
  {
    id: 'petite',
    name: 'Petite',
    emoji: '✨',
    description: 'Smaller frame, under 5\'4"',
    tip: 'Vertical stripes and monochrome looks elongate frame',
  },
  {
    id: 'plus',
    name: 'Plus Size',
    emoji: '💫',
    description: 'Fuller figure, all shapes welcome',
    tip: 'Wrap dresses and defined waistlines are universally flattering',
  },
];

const menShapes = [
  {
    id: 'athletic_m',
    name: 'Athletic',
    emoji: '💪',
    description: 'Broad shoulders, defined chest, narrow waist',
    tip: 'Fitted clothes that show your build work great',
  },
  {
    id: 'rectangle_m',
    name: 'Rectangle',
    emoji: '▭',
    description: 'Similar shoulder and waist width',
    tip: 'Layering adds dimension to your frame',
  },
  {
    id: 'oval_m',
    name: 'Oval',
    emoji: '🥚',
    description: 'Fuller midsection, narrower shoulders',
    tip: 'Vertical patterns and dark colours are slimming',
  },
  {
    id: 'triangle_m',
    name: 'Triangle',
    emoji: '🔻',
    description: 'Narrower shoulders, wider hips',
    tip: 'Structured shoulders and straight leg pants balance shape',
  },
  {
    id: 'inverted_m',
    name: 'Inverted Triangle',
    emoji: '🔺',
    description: 'Broad shoulders, narrow hips',
    tip: 'Straight or wide leg pants balance broad shoulders',
  },
];

const womenTopSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const womenBottomSizes = ['24', '26', '28', '30', '32', '34', '36', '38'];
const menTopSizes = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
const menBottomSizes = ['28', '30', '32', '34', '36', '38', '40', '42'];

function BodyTypeSelector({ user, onSave, onClose, existingProfile }) {
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState(
    existingProfile?.gender || null
  );
  const [selectedShape, setSelectedShape] = useState(
    existingProfile?.shape || null
  );
  const [topSize, setTopSize] = useState(
    existingProfile?.topSize || existingProfile?.size || null
  );
  const [bottomSize, setBottomSize] = useState(
    existingProfile?.bottomSize || null
  );
  const [saving, setSaving] = useState(false);

  const shapes = gender === 'men' ? menShapes : womenShapes;
  const topSizes = gender === 'men' ? menTopSizes : womenTopSizes;
  const bottomSizes = gender === 'men'
    ? menBottomSizes : womenBottomSizes;

  const selectedShapeData = shapes.find(
    s => s.id === selectedShape
  );

  const handleSave = async () => {
    if (!selectedShape || !topSize || !bottomSize) return;
    setSaving(true);

    try {
      const profile = {
        shape: selectedShape,
        shapeName: selectedShapeData?.name,
        gender,
        size: topSize,
        topSize,
        bottomSize,
      };

      if (user) {
        await saveBodyProfile(user.uid, profile);
      }

      onSave(profile);
      onClose();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>
              {step === 1 && 'Select your gender'}
              {step === 2 && 'Select your body shape'}
              {step === 3 && 'Select your sizes'}
            </h2>
            <p style={styles.subtitle}>
              {step === 1 && 'This helps us personalise recommendations for you'}
              {step === 2 && 'Choose the shape that best describes you'}
              {step === 3 && 'We use this to recommend the right size across platforms'}
            </p>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Progress Bar */}
        <div style={styles.progressBar}>
          {[1, 2, 3].map(s => (
            <div
              key={s}
              style={{
                ...styles.progressStep,
                background: s <= step ? '#C8715A' : '#EDE4DA',
              }}
            />
          ))}
        </div>

        {/* Step 1 — Gender */}
        {step === 1 && (
          <div style={styles.genderGrid}>
            {[
              { id: 'women', label: 'Women', emoji: '👩' },
              { id: 'men', label: 'Men', emoji: '👨' },
            ].map(g => (
              <button
                key={g.id}
                style={{
                  ...styles.genderBtn,
                  border: gender === g.id
                    ? '2px solid #C8715A'
                    : '2px solid #EDE4DA',
                  background: gender === g.id
                    ? '#FFF5F0' : 'white',
                }}
                onClick={() => setGender(g.id)}
              >
                <span style={styles.genderEmoji}>
                  {g.emoji}
                </span>
                <span style={styles.genderLabel}>
                  {g.label}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Step 2 — Body Shape */}
        {step === 2 && (
          <div style={styles.shapesGrid}>
            {shapes.map(shape => (
              <button
                key={shape.id}
                style={{
                  ...styles.shapeBtn,
                  border: selectedShape === shape.id
                    ? '2px solid #C8715A'
                    : '2px solid #EDE4DA',
                  background: selectedShape === shape.id
                    ? '#FFF5F0' : 'white',
                }}
                onClick={() => setSelectedShape(shape.id)}
              >
                <span style={styles.shapeEmoji}>
                  {shape.emoji}
                </span>
                <span style={styles.shapeName}>
                  {shape.name}
                </span>
                <span style={styles.shapeDesc}>
                  {shape.description}
                </span>
                {selectedShape === shape.id && (
                  <span style={styles.shapeTip}>
                    💡 {shape.tip}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Step 3 — Sizes */}
        {step === 3 && (
          <div style={styles.sizesContainer}>

            {/* Top Size */}
            <div style={styles.sizeSection}>
              <div style={styles.sizeSectionHeader}>
                <span style={styles.sizeSectionIcon}>👕</span>
                <div>
                  <h3 style={styles.sizeSectionTitle}>
                    Top Size
                  </h3>
                  <p style={styles.sizeSectionDesc}>
                    For shirts, kurtas, tops, blouses, dresses
                  </p>
                </div>
              </div>
              <div style={styles.sizeGrid}>
                {topSizes.map(size => (
                  <button
                    key={size}
                    style={{
                      ...styles.sizeBtn,
                      background: topSize === size
                        ? '#C8715A' : 'white',
                      color: topSize === size
                        ? 'white' : '#2C2420',
                      border: topSize === size
                        ? '2px solid #C8715A'
                        : '2px solid #EDE4DA',
                    }}
                    onClick={() => setTopSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={styles.divider} />

            {/* Bottom Size */}
            <div style={styles.sizeSection}>
              <div style={styles.sizeSectionHeader}>
                <span style={styles.sizeSectionIcon}>👖</span>
                <div>
                  <h3 style={styles.sizeSectionTitle}>
                    Bottom Size
                  </h3>
                  <p style={styles.sizeSectionDesc}>
                    {gender === 'men'
                      ? 'Waist size for jeans, trousers, shorts'
                      : 'Waist size for jeans, palazzos, skirts'}
                  </p>
                </div>
              </div>
              <div style={styles.sizeGrid}>
                {bottomSizes.map(size => (
                  <button
                    key={size}
                    style={{
                      ...styles.sizeBtn,
                      background: bottomSize === size
                        ? '#C8715A' : 'white',
                      color: bottomSize === size
                        ? 'white' : '#2C2420',
                      border: bottomSize === size
                        ? '2px solid #C8715A'
                        : '2px solid #EDE4DA',
                    }}
                    onClick={() => setBottomSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Note */}
            <div style={styles.sizeNote}>
              <span>📏</span>
              <span>
                Not sure of your size? Check the size chart
                on any platform before buying.
              </span>
            </div>

          </div>
        )}

        {/* Footer Buttons */}
        <div style={styles.footer}>
          {step > 1 && (
            <button
              style={styles.backBtn}
              onClick={() => setStep(step - 1)}
            >
              ← Back
            </button>
          )}

          {step < 3 ? (
            <button
              style={{
                ...styles.nextBtn,
                opacity: (
                  (step === 1 && !gender) ||
                  (step === 2 && !selectedShape)
                ) ? 0.5 : 1,
              }}
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !gender) ||
                (step === 2 && !selectedShape)
              }
            >
              Next →
            </button>
          ) : (
            <button
              style={{
                ...styles.saveBtn,
                opacity: (!topSize || !bottomSize || saving)
                  ? 0.5 : 1,
              }}
              onClick={handleSave}
              disabled={!topSize || !bottomSize || saving}
            >
              {saving ? 'Saving...' : '✦ Save My Profile'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(44,36,32,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '580px',
    maxHeight: '85vh',
    overflowY: 'auto',
    padding: '28px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C2420',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#8A7F7A',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#8A7F7A',
    cursor: 'pointer',
    padding: '4px',
    flexShrink: 0,
  },
  progressBar: {
    display: 'flex',
    gap: '6px',
    marginBottom: '24px',
  },
  progressStep: {
    flex: 1,
    height: '4px',
    borderRadius: '100px',
    transition: 'background 0.3s',
  },
  genderGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '24px',
  },
  genderBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '32px 20px',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: "'DM Sans', sans-serif",
  },
  genderEmoji: {
    fontSize: '48px',
  },
  genderLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2C2420',
  },
  shapesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  shapeBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '16px 12px',
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    fontFamily: "'DM Sans', sans-serif",
  },
  shapeEmoji: {
    fontSize: '28px',
  },
  shapeName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2C2420',
  },
  shapeDesc: {
    fontSize: '11px',
    color: '#8A7F7A',
    lineHeight: '1.4',
  },
  shapeTip: {
    fontSize: '11px',
    color: '#9B4D3A',
    lineHeight: '1.4',
    fontStyle: 'italic',
    marginTop: '4px',
  },
  sizesContainer: {
    marginBottom: '24px',
  },
  sizeSection: {
    marginBottom: '20px',
  },
  sizeSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
  },
  sizeSectionIcon: {
    fontSize: '28px',
    flexShrink: 0,
  },
  sizeSectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#2C2420',
    margin: '0 0 2px 0',
  },
  sizeSectionDesc: {
    fontSize: '11px',
    color: '#8A7F7A',
    margin: 0,
  },
  sizeGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  sizeBtn: {
    padding: '10px 16px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
    fontFamily: "'DM Sans', sans-serif",
    minWidth: '52px',
    textAlign: 'center',
  },
  divider: {
    height: '1px',
    background: '#EDE4DA',
    margin: '20px 0',
  },
  sizeNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F5F5F5',
    borderRadius: '10px',
    padding: '10px 14px',
    fontSize: '11px',
    color: '#8A7F7A',
    marginTop: '16px',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #EDE4DA',
  },
  backBtn: {
    background: 'white',
    border: '1.5px solid #EDE4DA',
    borderRadius: '12px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#2C2420',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  nextBtn: {
    background: '#2C2420',
    border: 'none',
    borderRadius: '12px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'opacity 0.2s',
  },
  saveBtn: {
    background: '#C8715A',
    border: 'none',
    borderRadius: '12px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'opacity 0.2s',
  },
};

export default BodyTypeSelector;